import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { resolveColor } from '../../theme/colorTokens.js';
import { ALIGN_OPTIONS, marginFields, visibilityField, weightField, } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `view-details` (block name "View full details") — config-by-config. A single "View full
 * details" link/button that points to the product page. Shrine exposes 8 inputs across 3 decorative
 * header dividers ("Solid/outlined button", "Text button", "Block margin"). All 8 are reproduced and
 * wired into the render:
 *
 *  - `style`              → our `style` (select: primary=Solid / secondary=Outline / text=Text, default text).
 *  - `enable_custom_color`→ our `enableCustomColor` (checkbox, default false).
 *  - `custom_color`       → our `customColor` (color, default #dd1d1d) — visible_if enable_custom_color.
 *  - `large_font_size`    → REPLACED by our `fontSize` range (see "TYPOGRAPHY" below).
 *  - `uppercase`          → our `uppercase` (checkbox, default false) — ungrouped, all three styles.
 *  - `display_arrow`      → our `displayArrow` (checkbox, default true) — group "Text button".
 *  - `margin_top`         → marginFields top (range 0–45 step 3, default 15) — group "Block margin".
 *  - `margin_bottom`      → marginFields bottom (range 0–45 step 3, default 0) — group "Block margin".
 *
 * ID MAPPING (our id ← Shrine id, kept stable — never renamed): enableCustomColor←enable_custom_color,
 * customColor←custom_color, displayArrow←display_arrow, marginTop←margin_top,
 * marginBottom←margin_bottom.
 *
 * ADDED (not in Shrine's 8, requested by the section brief "label + style + ALIGNMENT"): a `label`
 * text (Shrine hard-codes the localized "View full details" string — we let the merchant author it,
 * default "View full details") + an `alignment` select (left / center / right; Shrine left-aligns).
 * Both default to Shrine's fixed behaviour so an untouched block is pixel-faithful.
 *
 * ADDED (founder, 2026-09-16): `textColor` / `textCustomColor` — the LABEL's own colour. Shrine's
 * `custom_color` is an ACCENT: on Solid it paints the background and the text stays locked on
 * accent-contrast, on Outline it paints the border AND the text together. There was no way to say
 * "this word, that colour". The new pair overrides the label (and the arrow, which inherits
 * `currentColor`) in all three styles, and leaves the fill/border alone. Default is `default` — the
 * exact colour each style drew before the setting existed — so no live page repaints.
 *
 * The destination is the current product page, built host-side via `context.resolveUrl({type:'product',
 * slug})` from the shared product state — so it works on the real storefront and degrades to a plain
 * <a href> with no JS (SSR-safe; no client state needed).
 *
 * TYPOGRAPHY (founder, 2026-09-17 — the two controls that did nothing). Shrine scopes
 * `large_font_size` + `uppercase` under its "Solid/outlined button" divider: they change NOTHING on
 * the Text style, which is this block's DEFAULT style and the one the founder's store uses. Two live
 * switches that moved no pixel. So:
 *
 *  - `largeFontSize` (a 14px/17px toggle, Solid+Outline only) is GONE, replaced by `fontSize` — a real
 *    px range (10–40, default 16) honoured by all three styles.
 *  - `uppercase` leaves the "Solid/outlined button" group and applies to all three styles.
 *  - `weight` (the shared `weightField`) is added: `''` keeps each style's own weight — bold on the two
 *    buttons, medium on the Text link — so an untouched block is unchanged.
 *
 * MIGRATION NOTE: `fontSize` defaults to 16px, and `applyDefaults` fills it for blocks saved before
 * this field existed. The Text style already drew at `--aqx-theme-text-base` (16px), so it is
 * unchanged; a Solid/Outline button saved earlier moves 14px → 16px (17px → 16px if `large_font_size`
 * was on). A block schema has no `migrate()` hop (only sections do), and a slider that renders a size
 * it does not show would be the worse bug — so the 2px is taken, once, knowingly.
 *
 * `display_arrow` (a trailing → glyph) still applies ONLY to the Text style, as in Shrine.
 *
 * DEFERRED: none. This is a fully portable button-styling block — Shrine ships NO Shopify-platform
 * settings here (no animation, no glyph font, no CDN, no liquid/app hook, no share/cart AJAX). The
 * arrow is an inline SVG (not the Material-Symbols glyph font).
 */
export const viewDetailsBlockSchema = {
    type: 'view-details',
    name: 'View full details',
    max: 1,
    fields: [
        visibilityField,
        // ── (top, no header) ──────────────────────────────────────────────────────
        {
            id: 'label',
            type: 'text',
            label: 'Label',
            info: 'Link text. Leave empty to fall back to "View full details".',
            default: 'Voir tous les détails',
            defaultKey: 'section.view_all_details',
            maxLength: 60,
        },
        {
            id: 'style',
            type: 'select',
            label: 'Style',
            default: 'text',
            options: [
                { value: 'primary', label: 'Solid' },
                { value: 'secondary', label: 'Outline' },
                { value: 'text', label: 'Text' },
            ],
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'enableCustomColor',
            type: 'checkbox',
            label: 'Enable custom color',
            default: false,
        },
        {
            id: 'customColor',
            type: 'color',
            label: 'Custom color',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'enableCustomColor', equals: true },
        },
        {
            id: 'textColor',
            type: 'select',
            label: 'Text color',
            default: 'default',
            options: [
                { value: 'default', label: 'Default' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'textCustomColor',
            type: 'color',
            label: 'Custom text color',
            default: { ref: 'theme', token: 'ink' },
            visibleWhen: { field: 'textColor', equals: 'custom' },
        },
        // ── Typography (all three styles) ─────────────────────────────────────────
        {
            id: 'fontSize',
            type: 'range',
            label: 'Font size',
            min: 10,
            max: 40,
            step: 1,
            default: 16,
            unit: 'px',
        },
        weightField,
        {
            id: 'uppercase',
            type: 'checkbox',
            label: 'Uppercase',
            default: false,
        },
        // ── Text button ───────────────────────────────────────────────────────────
        {
            id: 'displayArrow',
            type: 'checkbox',
            label: 'Display arrow',
            group: 'Text button',
            default: true,
        },
        // ── Block margin ──────────────────────────────────────────────────────────
        ...marginFields(15, 0),
    ],
};
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
/** Trailing arrow for the Text style — small inline SVG (NOT the Material-Symbols glyph font). */
function arrowSvg() {
    return (_jsx("svg", { width: "1em", height: "1em", viewBox: "0 0 16 16", fill: "none", "aria-hidden": "true", style: { display: 'inline-block', flexShrink: 0 }, children: _jsx("path", { d: "M3 8h9M9 4.5 12.5 8 9 11.5", stroke: "currentColor", strokeWidth: "1.4", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
/**
 * "View full details" link to the product page. Renders an <a> styled as a Solid button, an Outline
 * button, or an underline-free Text link with an optional trailing arrow, per Shrine `style`.
 * SSR-safe: pure markup + one scoped <style> for hover affordances — degrades to a working link
 * without JS. Returns null outside a product container.
 */
const ViewDetailsBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-vd-${block.id}`;
    const label = str(bs.label) || 'Voir tous les détails';
    const style = str(bs.style, 'text'); // primary | secondary | text
    const justify = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    // enable_custom_color → custom_color. Drives accent on Solid (bg) / Outline + Text (text + border).
    const customOn = bs.enableCustomColor === true;
    const custom = customOn
        ? (resolveColor(bs.customColor) ?? 'var(--aqx-scheme-accent)')
        : null;
    // The LABEL's own colour, independent of the accent above. 'default' = whatever the chosen style
    // drew before this setting existed, so an untouched block is unchanged.
    const labelColor = str(bs.textColor, 'default') === 'custom'
        ? (resolveColor(bs.textCustomColor) ?? 'var(--aqx-theme-ink)')
        : null;
    // Typography — honoured by ALL THREE styles (the `large_font_size` checkbox this replaces, and the
    // `uppercase` one beside it, were scoped to Solid/Outline, so on the default Text style they were
    // dead controls). `weight` unset ('') keeps each style's own weight.
    const fontSize = `${num(bs.fontSize, 16)}px`;
    const weight = readWeight(bs);
    const uppercase = bs.uppercase === true;
    const caseStyle = uppercase
        ? { textTransform: 'uppercase', letterSpacing: '0.05em' }
        : {};
    // Text-only knob.
    const displayArrow = bs.displayArrow !== false;
    // Host-built product URL (degrades to "#" if the host can't resolve it).
    const href = context.resolveUrl({ type: 'product', slug: ps.product.slug }) || '#';
    const isButton = style === 'primary' || style === 'secondary';
    // Shared link baseline; per-style overrides below.
    const link = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.45em',
        textDecoration: 'none',
        fontFamily: 'inherit',
        cursor: 'pointer',
        lineHeight: 1.2,
    };
    if (style === 'primary') {
        // Solid: filled accent (or custom) button, accent-contrast text.
        Object.assign(link, {
            background: custom ?? 'var(--aqx-scheme-accent)',
            color: labelColor ?? 'var(--aqx-theme-accent-contrast, #fff)',
            border: '1px solid transparent',
            borderRadius: 'var(--aqx-theme-radius-button, 6px)',
            padding: '12px 26px',
            fontWeight: weight || 'var(--aqx-theme-fw-bold, 700)',
            fontSize,
            ...caseStyle,
        });
    }
    else if (style === 'secondary') {
        // Outline: transparent fill, accent (or custom / text) outline + matching text.
        const oc = custom ?? 'var(--aqx-scheme-text)';
        Object.assign(link, {
            background: 'transparent',
            color: labelColor ?? oc,
            border: `1px solid ${oc}`,
            borderRadius: 'var(--aqx-theme-radius-button, 6px)',
            padding: '12px 26px',
            fontWeight: weight || 'var(--aqx-theme-fw-bold, 700)',
            fontSize,
            ...caseStyle,
        });
    }
    else {
        // Text: bare link, underline on hover (via scoped CSS), custom or text colour.
        Object.assign(link, {
            background: 'transparent',
            color: labelColor ?? custom ?? 'var(--aqx-scheme-text)',
            border: 'none',
            padding: 0,
            fontWeight: weight || 'var(--aqx-theme-fw-medium, 600)',
            fontSize,
            ...caseStyle,
        });
    }
    // Scoped affordances: Solid/Outline hover dim; Text hover underline. Plus device visibility.
    const css = `.${cls}{display:flex;justify-content:${justify}}` +
        (isButton
            ? `.${cls} a{transition:opacity .15s ease}.${cls} a:hover{opacity:.85}`
            : `.${cls} a:hover{text-decoration:underline}`) +
        visibilityCss(cls, bs.visibility);
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: marginStyle(bs, 15, 0), children: _jsxs("a", { href: href, style: link, children: [_jsx("span", { children: label }), !isButton && displayArrow ? arrowSvg() : null] }) })] }));
};
export const viewDetailsBlock = {
    type: 'view-details',
    schema: viewDetailsBlockSchema,
    Component: ViewDetailsBlock,
    meta: { label: 'View full details' },
};
//# sourceMappingURL=index.js.map